import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Tag } from "@carbon/react";
import { CheckmarkFilled, CheckmarkOutline, CloseFilled, Renew } from "@carbon/icons-react";
import { IT_DOESNT_HAVE_SPEC, SAMPLE_STATE } from "../../utils";
import { ContactInformationCard, FileReader, Slide } from "..";
import Delegate from "../Material/Delegate/Delegate";
import { BuyerSellerResponse, MaterialOfferResponse } from "../../types";
import { useSeller } from "../../hooks/useValidateSeller";
import "./offerDisplay.scss";

type IsMaterialOfferProps = {
  materialOffer?: MaterialOfferResponse;
  valid?: boolean;
};

/**
 * The seller's own "my sample application" card on a material offer (Task 7) — replaces the
 * pre-Carbon Tailwind `dl`/`dt`/`dd` card with `.offer-detail-row` (same shared layout
 * `IsBuyerOffer.tsx`/`IsBuyerMultipleOffer.tsx` use) and uses Carbon icons
 * (`@carbon/icons-react`). Rendered by `components/Offer/Offer.tsx` and directly by
 * `containers/MaterialOffer/MaterialOffer.tsx` — both keep the unchanged
 * `{ materialOffer?, valid? }` prop contract. `.material-offer-card` reproduces
 * `MaterialOffer.tsx`'s own grid-column span (see that container's docstring) so this card keeps
 * its position in its 2-column grid.
 */
const IsMaterialOffer = ({
  materialOffer,
  valid = false,
}: IsMaterialOfferProps) => {
  const { t } = useTranslation();
  const [openDelegate, setOpenDelegate] = useState(false);

  const { data: seller } = useSeller();

  const isRejected = seller?.filter(
    (item: BuyerSellerResponse) =>
      item?.material?.id === materialOffer?.material?.id
  )[0]?.rejected;

  const handleDelegate = () => setOpenDelegate(true);
  const handleCloseDelegate = () => setOpenDelegate(false);

  return (
    <Fragment>
      <div className="material-offer-card">
        <div className="material-offer-card__inner">
          {isRejected && (
            <div className="material-offer-card__rejected">
              <Tag type="red" size="md">
                {t("tenders.seller.rejected")}
              </Tag>
            </div>
          )}
          <p className="material-offer-card__heading">
            {t("tenders.seller.myApplication")}
          </p>

          <h1 className="material-offer-card__title">
            {materialOffer?.material?.globalMaterialName}
          </h1>
          {valid && (
            <div className="material-offer-card__validated">
              <Tag type="green" size="md" renderIcon={CheckmarkFilled}>
                {t("offer.validatedFlag")}
              </Tag>
            </div>
          )}

          <div className="offer-detail-row">
            <p className="offer-detail-row__label">{t("offer.origin")}</p>
            <p className="offer-detail-row__value">
              {materialOffer?.origin?.name}
            </p>
          </div>

          {materialOffer && materialOffer?.certifications?.length > 0 && (
            <div className="offer-detail-row">
              <p className="offer-detail-row__label">
                {t("certifications.certifications")}
              </p>
              <p className="offer-detail-row__value">
                {materialOffer?.certifications
                  ?.map((cert) => cert.materialCertification.name)
                  .join(", ")}
              </p>
            </div>
          )}

          {materialOffer?.comment && (
            <div className="offer-detail-row">
              <p className="offer-detail-row__label">{t("offer.comments")}</p>
              <p className="offer-detail-row__value">
                {materialOffer.comment}
              </p>
            </div>
          )}

          {materialOffer?.specificationFileUrl && (
            <div className="offer-detail-row">
              <p className="offer-detail-row__label">
                {t("newMaterialForm.specification")}
              </p>
              <div className="offer-detail-row__value">
                {materialOffer?.specificationFileUrl ? (
                  <FileReader fileKey={materialOffer?.specificationFileUrl} />
                ) : (
                  <p>{IT_DOESNT_HAVE_SPEC}</p>
                )}
              </div>
            </div>
          )}

          {materialOffer && materialOffer?.state === SAMPLE_STATE.REQUESTED && (
            <ContactInformationCard
              user={materialOffer?.material?.owner}
              isSample
            />
          )}
          {materialOffer?.state === SAMPLE_STATE.VERIFIED && (
            <div className="material-offer-card__state-banner">
              <CheckmarkOutline
                size={28}
                className="material-offer-card__state-icon--verified"
              />
              <p>{t("offer.sampleVerified")}</p>
            </div>
          )}
          {materialOffer?.state === SAMPLE_STATE.REJECTED && (
            <div className="material-offer-card__state-banner">
              <CloseFilled
                size={28}
                className="material-offer-card__state-icon--rejected"
              />
              <p>{t("offer.sampleRejected")}</p>
            </div>
          )}
        </div>
        <div className="material-offer-card__actions">
          <Button kind="tertiary" size="sm" renderIcon={Renew} onClick={handleDelegate}>
            {t("delegate.delegate")}
          </Button>
        </div>
      </div>
      <Slide open={openDelegate} setOpen={setOpenDelegate}>
        <Delegate
          onCloseDelegate={handleCloseDelegate}
          isDelegateMaterialOffer
          materialOffer={materialOffer}
        />
      </Slide>
    </Fragment>
  );
};

export default IsMaterialOffer;
